import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sun, Cloud, CloudRain, CloudDrizzle, CloudLightning, CloudFog, Snowflake, CloudSun,
  Wind, Droplets, MapPin, Loader2,
} from "lucide-react";

const WEATHER_ICONS = {
  "sun": Sun, "cloud-sun": CloudSun, "cloud": Cloud, "cloud-rain": CloudRain,
  "cloud-drizzle": CloudDrizzle, "cloud-lightning": CloudLightning,
  "cloud-fog": CloudFog, "snowflake": Snowflake,
};

const DAY_NAMES = {
  pt: { monday: "Segunda", tuesday: "Terça", wednesday: "Quarta", thursday: "Quinta", friday: "Sexta", saturday: "Sábado", sunday: "Domingo" },
  en: { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" },
  es: { monday: "Lunes", tuesday: "Martes", wednesday: "Miércoles", thursday: "Jueves", friday: "Viernes", saturday: "Sábado", sunday: "Domingo" },
  fr: { monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche" },
};

const WEATHER_LABELS = {
  pt: { clear: "Céu limpo", mostly_clear: "Céu quase limpo", partly_cloudy: "Parcialmente nublado", overcast: "Nublado", fog: "Nevoeiro", light_drizzle: "Chuvisco", drizzle: "Chuvisco", heavy_drizzle: "Chuvisco forte", freezing_drizzle: "Chuvisco gelado", light_rain: "Chuva ligeira", rain: "Chuva", heavy_rain: "Chuva forte", freezing_rain: "Chuva gelada", light_snow: "Neve ligeira", snow: "Neve", heavy_snow: "Neve forte", snow_grains: "Granizo", light_showers: "Aguaceiros", showers: "Aguaceiros", heavy_showers: "Aguaceiros fortes", snow_showers: "Aguaceiros de neve", heavy_snow_showers: "Aguaceiros de neve fortes", thunderstorm: "Trovoada", thunderstorm_hail: "Trovoada c/ granizo", unknown: "Variável" },
  en: { clear: "Clear", mostly_clear: "Mostly clear", partly_cloudy: "Partly cloudy", overcast: "Overcast", fog: "Fog", light_drizzle: "Light drizzle", drizzle: "Drizzle", heavy_drizzle: "Heavy drizzle", freezing_drizzle: "Freezing drizzle", light_rain: "Light rain", rain: "Rain", heavy_rain: "Heavy rain", freezing_rain: "Freezing rain", light_snow: "Light snow", snow: "Snow", heavy_snow: "Heavy snow", snow_grains: "Snow grains", light_showers: "Showers", showers: "Showers", heavy_showers: "Heavy showers", snow_showers: "Snow showers", heavy_snow_showers: "Heavy snow showers", thunderstorm: "Thunderstorm", thunderstorm_hail: "Thunderstorm w/ hail", unknown: "Variable" },
  es: { clear: "Despejado", mostly_clear: "Casi despejado", partly_cloudy: "Parcialmente nublado", overcast: "Nublado", fog: "Niebla", light_drizzle: "Llovizna", drizzle: "Llovizna", heavy_drizzle: "Llovizna fuerte", freezing_drizzle: "Llovizna helada", light_rain: "Lluvia ligera", rain: "Lluvia", heavy_rain: "Lluvia fuerte", freezing_rain: "Lluvia helada", light_snow: "Nieve ligera", snow: "Nieve", heavy_snow: "Nieve fuerte", snow_grains: "Granizo", light_showers: "Chubascos", showers: "Chubascos", heavy_showers: "Chubascos fuertes", snow_showers: "Chubascos de nieve", heavy_snow_showers: "Chubascos de nieve fuertes", thunderstorm: "Tormenta", thunderstorm_hail: "Tormenta c/ granizo", unknown: "Variable" },
  fr: { clear: "Dégagé", mostly_clear: "Presque dégagé", partly_cloudy: "Partiellement nuageux", overcast: "Couvert", fog: "Brouillard", light_drizzle: "Bruine", drizzle: "Bruine", heavy_drizzle: "Bruine forte", freezing_drizzle: "Bruine verglaçante", light_rain: "Pluie légère", rain: "Pluie", heavy_rain: "Forte pluie", freezing_rain: "Pluie verglaçante", light_snow: "Neige légère", snow: "Neige", heavy_snow: "Forte neige", snow_grains: "Grêle", light_showers: "Averses", showers: "Averses", heavy_showers: "Fortes averses", snow_showers: "Averses de neige", heavy_snow_showers: "Fortes averses de neige", thunderstorm: "Orage", thunderstorm_hail: "Orage avec grêle", unknown: "Variable" },
};

export function WeeklyWeatherModal({ open, onOpenChange }) {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang = i18n.language?.split("-")[0] || "pt";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    setLoading(true);
    setError(null);
    api.get("/api/weather/weekly")
      .then(res => {
        if (res.data?.available) {
          setData(res.data);
        } else {
          setError(res.data?.reason || "unavailable");
        }
      })
      .catch(() => setError("network"))
      .finally(() => setLoading(false));
  }, [open, isAuthenticated]);

  const dayNames = DAY_NAMES[lang] || DAY_NAMES.pt;
  const labels = WEATHER_LABELS[lang] || WEATHER_LABELS.pt;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="weekly-weather-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <CloudSun className="h-5 w-5 text-primary" />
            {lang === "pt" ? "Tempo dos próximos 7 dias" : lang === "es" ? "Tiempo de los próximos 7 días" : lang === "fr" ? "Météo des 7 prochains jours" : "Weather for the next 7 days"}
          </DialogTitle>
          {data?.city && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <MapPin className="h-3 w-3" />{data.city}
            </p>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {error === "no_coordinates"
              ? (lang === "pt" ? "Adiciona a tua localização no Perfil para ver a previsão." : lang === "es" ? "Añade tu ubicación en el Perfil para ver la previsión." : lang === "fr" ? "Ajoute ta localisation dans ton Profil pour voir les prévisions." : "Add your location in Profile to see the forecast.")
              : (lang === "pt" ? "Não foi possível obter a previsão." : lang === "es" ? "No se pudo obtener la previsión." : lang === "fr" ? "Impossible d'obtenir les prévisions." : "Could not load forecast.")}
          </div>
        )}

        {data?.forecast?.days && (
          <div className="space-y-2 mt-2">
            {data.forecast.days.map((day) => {
              const Icon = WEATHER_ICONS[day.icon] || Cloud;
              const condition = labels[day.weather_key] || labels.unknown;
              const isToday = day.date === today;
              const isWeekend = day.day_name === "saturday" || day.day_name === "sunday";

              return (
                <div
                  key={day.date}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                    day.is_good
                      ? "bg-amber-400/10 border-amber-400/25"
                      : isWeekend
                        ? "bg-card/80 border-border/50"
                        : "bg-card/40 border-border/30"
                  }`}
                  data-testid={`weekly-day-${day.day_name}`}
                >
                  <Icon className={`h-7 w-7 shrink-0 ${day.is_good ? "text-amber-400" : "text-muted-foreground"}`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-semibold ${isToday ? "text-primary" : "text-foreground"}`}>
                        {dayNames[day.day_name]}
                      </span>
                      <span className="text-xs text-muted-foreground">{day.date.slice(8)}/{day.date.slice(5,7)}</span>
                      {isToday && (
                        <span className="text-[10px] font-medium uppercase tracking-wider text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded">
                          {lang === "pt" ? "Hoje" : lang === "es" ? "Hoy" : lang === "fr" ? "Auj." : "Today"}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{condition}</span>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-foreground">
                      {Math.round(day.temp_max)}°
                      <span className="font-normal text-muted-foreground ml-0.5">/ {Math.round(day.temp_min)}°</span>
                    </p>
                    <div className="flex items-center justify-end gap-2 mt-0.5">
                      {day.precipitation_prob > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px] text-sky-400">
                          <Droplets className="h-3 w-3" />{day.precipitation_prob}%
                        </span>
                      )}
                      {day.wind_max > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                          <Wind className="h-3 w-3" />{Math.round(day.wind_max)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
